import { formatDisplayEmail } from '@bs/core'
import { Button, pantryColors } from '@pantry/ui'
import React, { useState } from 'react'
import { useDebounce } from '../../../../hooks/useDebounce'
import type {
  LoyaltyFlowCart,
  LoyaltyFlowCustomer,
  LoyaltyFlowCustomerIdentity,
} from '../../../../types/loyaltyMarketing'
import { ProductThumb } from '../../../Customer/components/profile/tabs/behavior/v2/panels/products/ProductThumb'
import {
  useLoyaltyFlowCustomerDetailQuery,
  useLoyaltyFlowCustomersQuery,
} from '../../hooks/useLoyaltyMarketing'
import { ActivityMessage, ActivityRow } from './CampaignActivityList'
import {
  STATUS_TEXT,
  eventLabel,
  formatWhen,
  reasonText,
  statusColor,
} from './FlowActivityList'

const p = pantryColors

const customerLabel = (c: {
  name?: string
  email: string
  customer_id: string
}): string => c.name || formatDisplayEmail(c.email) || c.customer_id

const CART_STATUS: Record<string, string> = {
  active: 'Still in cart',
  converted: 'Order placed',
  expired: 'Cart expired',
}

const UnsubscribedBadge: React.FC = () => (
  <span
    style={{
      fontSize: 10.5,
      fontWeight: 600,
      padding: '1px 7px',
      borderRadius: 10,
      background: p.red[50],
      color: p.red[700],
      whiteSpace: 'nowrap',
    }}
  >
    Unsubscribed
  </span>
)

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 700,
      color: p.gray[500],
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      margin: '16px 0 4px',
    }}
  >
    {children}
  </div>
)

const CustomerRow: React.FC<{
  customer: LoyaltyFlowCustomer
  onOpen: () => void
}> = ({ customer, onOpen }) => {
  const label = customerLabel(customer)
  const email = formatDisplayEmail(customer.email)
  const counts = [
    `${customer.sent} sent`,
    ...(customer.skipped ? [`${customer.skipped} skipped`] : []),
    ...(customer.failed ? [`${customer.failed} failed`] : []),
  ].join(' · ')
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'flex',
        width: '100%',
        gap: 12,
        padding: '11px 0',
        border: 'none',
        borderBottom: `1px solid ${p.gray[100]}`,
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          flexShrink: 0,
          marginTop: 5,
          background: statusColor(customer.last_status),
        }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            fontSize: 13,
            fontWeight: 600,
            color: p.gray[800],
          }}
        >
          {label}
          {!customer.subscribed && customer.email && <UnsubscribedBadge />}
        </span>
        {email && email !== label && (
          <span style={{ display: 'block', fontSize: 12, color: p.gray[500] }}>
            {email}
          </span>
        )}
        <span
          style={{
            display: 'block',
            fontSize: 12,
            color: p.gray[500],
            marginTop: 2,
          }}
        >
          {counts}
        </span>
      </span>
      <span style={{ textAlign: 'right', flexShrink: 0 }}>
        <span style={{ display: 'block', fontSize: 12, color: p.gray[700] }}>
          {STATUS_TEXT[customer.last_status] ?? customer.last_status}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: p.gray[400] }}>
          {formatWhen(customer.last_at)}
        </span>
      </span>
    </button>
  )
}

const CartDetail: React.FC<{ cart: LoyaltyFlowCart | null }> = ({ cart }) => {
  if (!cart)
    return (
      <div style={{ fontSize: 13, color: p.gray[500], padding: '8px 0' }}>
        No cart found for this customer.
      </div>
    )
  return (
    <div>
      {cart.items.length === 0 ? (
        <div style={{ fontSize: 13, color: p.gray[500], padding: '8px 0' }}>
          This cart has no items.
        </div>
      ) : (
        cart.items.map((item, i) => (
          <div
            key={`${item.item_name}-${item.item_variant}-${i}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '9px 0',
              borderBottom: `1px solid ${p.gray[100]}`,
            }}
          >
            <ProductThumb
              src={item.item_image}
              name={item.item_name}
              size={38}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{ fontSize: 13, fontWeight: 600, color: p.gray[800] }}
              >
                {item.item_name}
              </div>
              <div style={{ fontSize: 12, color: p.gray[500] }}>
                {[item.item_variant, `Qty ${item.item_qty}`]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div style={{ fontSize: 13, color: p.gray[700] }}>
              {item.item_price}
            </div>
          </div>
        ))
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          padding: '10px 0 0',
          fontSize: 13,
        }}
      >
        <span style={{ color: p.gray[500] }}>
          {CART_STATUS[cart.status] ?? cart.status}
        </span>
        <span style={{ fontWeight: 700, color: p.gray[800] }}>
          Cart total: {cart.total || '—'}
        </span>
      </div>
    </div>
  )
}

const CustomerDetail: React.FC<{
  storeId: string
  flowId: string
  customer: LoyaltyFlowCustomerIdentity
  onBack: () => void
}> = ({ storeId, flowId, customer, onBack }) => {
  const { data, isLoading, isError } = useLoyaltyFlowCustomerDetailQuery(
    storeId,
    flowId,
    customer,
  )
  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack}>
        ← All customers
      </Button>
      {isLoading && (
        <ActivityMessage loading>Loading customer…</ActivityMessage>
      )}
      {isError && (
        <ActivityMessage>
          Couldn't load this customer. Please try again.
        </ActivityMessage>
      )}
      {data && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
              marginTop: 10,
              fontSize: 14,
              fontWeight: 700,
              color: p.gray[800],
            }}
          >
            {customerLabel(data.customer)}
            {!data.customer.subscribed && data.customer.email && (
              <UnsubscribedBadge />
            )}
          </div>
          {data.customer.name && (
            <div style={{ fontSize: 12, color: p.gray[500] }}>
              {formatDisplayEmail(data.customer.email)}
            </div>
          )}
          <SubHeading>Cart</SubHeading>
          <CartDetail cart={data.cart} />
          <SubHeading>Activity</SubHeading>
          {data.events.map((event) => (
            <ActivityRow
              key={event.id}
              color={statusColor(event.status)}
              text={eventLabel(event)}
              sub={reasonText(event.reason)}
              when={formatWhen(event.created_at)}
            />
          ))}
        </>
      )}
    </div>
  )
}

// Activity › Customer view: the customers this automation acted on, with
// search; clicking one shows their activity and cart.
export const FlowCustomerView: React.FC<{
  storeId: string
  flowId: string
  enabled?: boolean
}> = ({ storeId, flowId, enabled = true }) => {
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search.trim(), 300)
  const [selected, setSelected] = useState<LoyaltyFlowCustomerIdentity | null>(
    null,
  )
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLoyaltyFlowCustomersQuery(storeId, flowId, debounced, {
    enabled: enabled && !selected,
  })
  const customers = (data?.pages ?? []).flatMap((page) => page.customers)

  if (selected)
    return (
      <CustomerDetail
        storeId={storeId}
        flowId={flowId}
        customer={selected}
        onBack={() => setSelected(null)}
      />
    )

  return (
    <div>
      <input
        type="search"
        aria-label="Search customers"
        placeholder="Search by name, email or customer ID"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '8px 12px',
          marginBottom: 4,
          border: `1px solid ${p.gray[200]}`,
          borderRadius: 6,
          fontSize: 13,
          outline: 'none',
        }}
      />
      {isLoading && (
        <ActivityMessage loading>Loading customers…</ActivityMessage>
      )}
      {isError && (
        <ActivityMessage>
          Couldn't load customers. Please try again.
        </ActivityMessage>
      )}
      {!isLoading && !isError && customers.length === 0 && (
        <ActivityMessage>
          {debounced ? 'No customers match your search.' : 'No customers yet.'}
        </ActivityMessage>
      )}
      {customers.map((customer) => (
        <CustomerRow
          key={`${customer.customer_id}|${customer.email}`}
          customer={customer}
          onOpen={() =>
            setSelected({
              customer_id: customer.customer_id,
              email: customer.email,
            })
          }
        />
      ))}
      {hasNextPage && (
        <div
          style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}
        >
          <Button
            variant="ghost"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}
